import { css } from "@emotion/css";
import { isEqual } from "lodash";
import { forwardRef, ReactNode, useEffect, useRef, useState } from "react";
import { Form, FormProps, FormContext } from "react-aria-components";

import { CascadiaFormSubmissionError } from "./errors";
import { useLocalDraft } from "@certn/utils";
import { useParams } from "react-router-dom";

type Errors = FormProps["validationErrors"];

interface RenderProps<Data> {
  submitErrors: Errors;
  setSubmitErrors(errors: Errors): void;
  setValues(values: Data): void;
  values: Data;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  submitted?: boolean;
  setSubmitted?: (submitted: boolean) => void;
  isSubmitting?: boolean;
  touched: Record<string, boolean>;
  setTouched(path: string): void;
  isSummaryOverride: string[];
  setIsSummaryOverride(path: string, isSummary: boolean): void;
}

export interface CascadiaFormProps<Data>
  extends Omit<FormProps, "onSubmit" | "children"> {
  initialValues: Data;
  onChange?: (data: Data) => void;
  onSubmit?: (values: Data) => Promise<void> | void;
  children?: ReactNode | ((values: RenderProps<Data>) => ReactNode);
  formID?: string;
}

export const CascadiaForm = forwardRef(function CascadiaForm<
  Data extends object,
>(props: CascadiaFormProps<Data>, ref: React.ForwardedRef<HTMLFormElement>) {
  const {
    onChange,
    onSubmit,
    children,
    formID,
    initialValues,
    id,
    ...formProps
  } = props;

  const { taskId } = useParams();

  const { draftValues, saveDraft, clearDraft } = useLocalDraft<Data>(taskId);

  const [isSubmitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<Data>({
    ...initialValues,
    ...draftValues,
  });
  // handleSubmit reads from this ref instead of the `values` state directly,
  // so callers that do `setValues(next); handleSubmit()` synchronously
  // observe `next` instead of the previous render's state. (See
  // FitnessProprietyControl for the case where this matters.)
  const valuesRef = useRef<Data>(values);
  valuesRef.current = values;
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSummaryOverride, setIsSummaryOverride] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<Errors>({});

  useEffect(() => {
    // check if values have changed and call onChange
    if (!isEqual(values, initialValues)) {
      saveDraft(values);
      onChange?.(values);
    }
  }, [values]);

  useEffect(() => {
    // If the user has made changes, don't reset the form
    if (!isDirty) {
      setValues(initialValues);
      setIsDirty(false);
    }
  }, [initialValues]);

  useEffect(() => {
    // we want to reset submitted when form changes such as the currentSection
    setSubmitted(false);

    // Only reset to initialValues if no draft exists, otherwise preserve draft data
    setValues({ ...initialValues, ...draftValues });
  }, [formID]);

  async function handleSubmit(e?: React.FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    e?.stopPropagation();
    setSubmitErrors({});

    // Read from the ref so a synchronous `setValues(next); handleSubmit()`
    // pair sees the just-set values instead of the previous render's snapshot.
    const submittedValues = valuesRef.current;
    if (onSubmit && submittedValues) {
      try {
        setSubmitting(true);
        await onSubmit(submittedValues);
        clearDraft();
      } catch (e) {
        if (e instanceof CascadiaFormSubmissionError) {
          setSubmitErrors(e.errors);
          return;
        }

        throw e;
      } finally {
        setSubmitted(true);
        setSubmitting(false);
      }
    }
  }

  return (
    <FormContext.Provider value={{}}>
      <Form
        ref={ref}
        onSubmit={handleSubmit}
        validationErrors={submitErrors}
        className={styles.form}
        onInvalid={() => {
          setSubmitted(true);
        }}
        {...formProps}
      >
        {typeof children === "function"
          ? children({
              setValues: (next) => {
                setIsDirty(true);
                // Update the ref synchronously so a same-tick handleSubmit()
                // call sees the new values without waiting for React to
                // commit the setValues state update.
                valuesRef.current = next;
                setValues(next);
              },
              submitErrors,
              setSubmitErrors,
              values,
              handleSubmit,
              submitted,
              setSubmitted,
              isSubmitting,
              touched,
              setTouched(path: string) {
                setSubmitErrors((prev) => {
                  const next = { ...prev };
                  delete next[path];
                  return next;
                });

                setTouched((prev) => ({ ...prev, [path]: true }));
                saveDraft(values);
              },
              isSummaryOverride,
              setIsSummaryOverride(path: string, isSummary: boolean) {
                isSummary
                  ? setIsSummaryOverride((prev) =>
                      prev.includes(path) ? prev : [...prev, path]
                    )
                  : setIsSummaryOverride((prev) =>
                      prev.filter((p) => p !== path)
                    );
              },
            })
          : children}
      </Form>
    </FormContext.Provider>
  );
});

const styles = {
  form: css({ display: "flex", flexDirection: "column" }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
  }),
};
